import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { getProduct } from "../lib/dataStore";
import { useCart } from "../context/AppContext";
import { toast } from "sonner";
import {
  ArrowLeft,
  ShoppingBag,
  Youtube,
  Truck,
  ChevronLeft,
  ChevronRight,
  Ruler,
  Scale,
  Palette,
  Layers,
  Maximize2,
  X,
  Play,
  Pause,
  Sparkles,
  Check,
} from "lucide-react";
import { normaliseImageUrl, handleImageError } from "../lib/driveImage";
import { isSoldOut, normalizeProduct, parseComboQty, primaryImage } from "../lib/productHelpers";

export default function ProductDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [rawProduct, setRawProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [variantIdx, setVariantIdx] = useState(0);
  const [activeImgIdx, setActiveImgIdx] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const thumbScrollRef = useRef(null);
  const variantScrollRef = useRef(null);
  const pageTopRef = useRef(null);
  const { add } = useCart();
  const nav = useNavigate();

  const scrollToPageTop = () => {
    if (pageTopRef.current) {
      pageTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } catch {
      window.scrollTo(0, 0);
    }
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getProduct(id).then((prod) => {
      setRawProduct(prod);
      if (prod) {
        const norm = normalizeProduct(prod);
        const queryVariant = searchParams.get("variant");
        if (queryVariant && norm.variants?.length) {
          const matchIdx = norm.variants.findIndex(
            (v) => v.tag?.toLowerCase() === queryVariant.toLowerCase()
          );
          setVariantIdx(matchIdx >= 0 ? matchIdx : 0);
        } else {
          setVariantIdx(0);
        }
      }
      setActiveImgIdx(0);
    });
  }, [id, searchParams]);

  const p = rawProduct ? normalizeProduct(rawProduct) : null;
  const variant = p?.variants?.[variantIdx] || p?.variants?.[0];

  // Determine images to display (variant-specific or product level)
  const images = useMemo(() => {
    if (variant?.images && variant.images.length > 0) return variant.images;
    if (p?.images && p.images.length > 0) return p.images;
    return [];
  }, [variant?.images, p?.images]);

  const handlePrev = useCallback((e) => {
    e?.stopPropagation?.();
    setActiveImgIdx((prev) => (prev === 0 ? Math.max(0, images.length - 1) : prev - 1));
  }, [images.length]);

  const handleNext = useCallback((e) => {
    e?.stopPropagation?.();
    setActiveImgIdx((prev) => (images.length === 0 ? 0 : (prev + 1) % images.length));
  }, [images.length]);

  // Auto-loop image carousel every 3.5 seconds if multiple images exist and not paused
  useEffect(() => {
    const len = images.length;
    if (len <= 1 || isPaused) return;
    const timer = setInterval(() => {
      setActiveImgIdx((prev) => (prev + 1) % len);
    }, 3500);
    return () => clearInterval(timer);
  }, [images.length, isPaused]);

  // Keyboard navigation for manual image switching (Left / Right arrow keys)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (images.length <= 1) return;
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [images.length, handleNext, handlePrev]);

  // Auto scroll active thumbnail horizontally into visible center of thumbnail bar (container-only)
  useEffect(() => {
    if (!thumbScrollRef.current) return;
    const container = thumbScrollRef.current;
    const activeEl = container.children?.[activeImgIdx];
    if (activeEl) {
      const targetLeft = activeEl.offsetLeft - (container.clientWidth / 2) + (activeEl.clientWidth / 2);
      container.scrollTo({ left: Math.max(0, targetLeft), behavior: "smooth" });
    }
  }, [activeImgIdx]);

  // Auto scroll active variant card horizontally inside variant row (container-only)
  useEffect(() => {
    if (!variantScrollRef.current) return;
    const container = variantScrollRef.current;
    const activeEl = container.children?.[variantIdx];
    if (activeEl) {
      const targetLeft = activeEl.offsetLeft - (container.clientWidth / 2) + (activeEl.clientWidth / 2);
      container.scrollTo({ left: Math.max(0, targetLeft), behavior: "smooth" });
    }
  }, [variantIdx]);

  if (!rawProduct || !p) return <div className="max-w-7xl mx-auto px-6 py-24 text-center text-muted-foreground">Loading…</div>;

  const soldOut = isSoldOut(p);
  const variantOut = Number(variant?.stock || 0) <= 0;

  // Reset active image index, trigger loading animation, and scroll to top when variant changes
  const handleVariantSelect = (i) => {
    scrollToPageTop();
    if (i === variantIdx) return;
    setIsImageLoading(true);
    setVariantIdx(i);
    setActiveImgIdx(0);
    setTimeout(() => {
      scrollToPageTop();
    }, 60);
  };

  const handleImageLoaded = () => {
    // Graceful release so users see a smooth transition between variant image switches
    setTimeout(() => {
      setIsImageLoading(false);
    }, 180);
  };

  // Touch swipe support for mobile
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 40) handleNext();
    if (distance < -40) handlePrev();
  };

  const showVariants = (p.variants?.length || 0) > 1 || (p.variants?.[0]?.tag && p.variants[0].tag !== "Default");

  // Dynamic price & MRP based on selected variant
  const price = variant?.price != null && variant?.price !== "" ? Number(variant.price) : Number(p.price || 0);
  const mrp = variant?.mrp != null && variant?.mrp !== "" ? Number(variant.mrp) : Number(p.mrp || price);
  const hasDiscount = mrp > price;
  const discountPct = hasDiscount ? Math.round(((mrp - price) / mrp) * 100) : 0;
  const shippingCharge = Number(p.shippingCharge || 0);

  // Specifications
  const bagType = p.bagType || "";
  const material = p.material || "";
  const size = p.size || "";
  const weight = p.weight || "";
  const colour = p.colour || (variant?.tag && variant.tag !== "Default" ? variant.tag : "");
  const hasAnySpec = !!(bagType || material || size || weight || colour);

  return (
    <div ref={pageTopRef} className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to shop
      </Link>
      <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
        {/* Left: Product Images / Auto & Manual Carousel */}
        <div className="grid gap-4">
          {/* Main Stage: Image fits perfectly according to its natural size / aspect ratio without cropping */}
          <div
            className="relative w-full h-[380px] sm:h-[480px] md:h-[520px] bg-muted/20 border border-border rounded-2xl flex items-center justify-center p-3 sm:p-6 overflow-hidden select-none group shadow-xs"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Loading Animation Overlay while Variant Image is Loading */}
            {isImageLoading && (
              <div
                data-testid="variant-loading-animation"
                className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-background/60 backdrop-blur-[3px] transition-all duration-300 pointer-events-none"
              >
                <div className="flex flex-col items-center gap-2.5 p-4 sm:p-5 rounded-2xl bg-card/95 border border-border/80 shadow-md">
                  <div className="relative flex items-center justify-center w-12 h-12">
                    <div className="w-10 h-10 border-3 border-terracotta/25 border-t-terracotta rounded-full animate-spin" />
                    <Sparkles className="w-4 h-4 text-terracotta absolute animate-pulse" />
                  </div>
                  <div className="text-center space-y-0.5">
                    <div className="text-xs font-semibold text-foreground tracking-wide flex items-center justify-center gap-1.5">
                      <span>Loading variant image</span>
                      <span className="flex gap-0.5">
                        <span className="w-1 h-1 rounded-full bg-terracotta animate-bounce [animation-delay:-0.3s]" />
                        <span className="w-1 h-1 rounded-full bg-terracotta animate-bounce [animation-delay:-0.15s]" />
                        <span className="w-1 h-1 rounded-full bg-terracotta animate-bounce" />
                      </span>
                    </div>
                    <div className="text-[11px] text-muted-foreground font-medium">
                      {variant?.tag && variant.tag !== "Default" ? variant.tag : "Updating view…"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <img
              key={`${variantIdx}-${activeImgIdx}-${images[activeImgIdx] || ""}`}
              src={normaliseImageUrl(images[activeImgIdx]) || "https://images.pexels.com/photos/21326994/pexels-photo-21326994.jpeg"}
              alt={p.name}
              className={`max-w-full max-h-full w-auto h-auto object-contain transition-all duration-300 drop-shadow-xs cursor-pointer ${
                isImageLoading ? "opacity-25 scale-98 blur-xs" : "opacity-100 scale-100 blur-0"
              }`}
              onClick={() => setLightboxOpen(true)}
              referrerPolicy="no-referrer"
              onLoad={handleImageLoaded}
              onError={(e) => {
                setIsImageLoading(false);
                handleImageError(e, images[activeImgIdx]);
              }}
            />

            {/* Lightbox full-size view button */}
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              aria-label="View full image"
              title="Click to view full size"
              className="absolute top-3 right-3 p-2 rounded-full bg-background/80 hover:bg-background text-foreground border border-border opacity-0 group-hover:opacity-100 transition shadow-xs z-10"
            >
              <Maximize2 className="w-4 h-4" />
            </button>

            {/* Manual Left & Right Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous image"
                  onClick={handlePrev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-background/90 hover:bg-background text-foreground shadow-md border border-border flex items-center justify-center transition-all opacity-80 group-hover:opacity-100 focus:opacity-100 hover:scale-105 z-10"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  aria-label="Next image"
                  onClick={handleNext}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-background/90 hover:bg-background text-foreground shadow-md border border-border flex items-center justify-center transition-all opacity-80 group-hover:opacity-100 focus:opacity-100 hover:scale-105 z-10"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>

                {/* Dot Indicators and Index Pill */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-background/85 backdrop-blur-xs px-3 py-1 rounded-full z-10 border border-border shadow-xs">
                  <span className="text-[11px] font-mono font-medium text-muted-foreground mr-1">
                    {activeImgIdx + 1} / {images.length}
                  </span>
                  {images.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      aria-label={`Go to image ${i + 1}`}
                      onClick={() => setActiveImgIdx(i)}
                      className={`h-1.5 rounded-full transition-all ${
                        i === activeImgIdx ? "bg-terracotta w-4" : "bg-muted-foreground/40 w-1.5 hover:bg-muted-foreground"
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Thumbnail Gallery with Manual Horizontal Scroll & Scroll Buttons */}
          {images?.length > 1 && (
            <div className="space-y-2">
              <div className="relative flex items-center">
                {images.length > 4 && (
                  <button
                    type="button"
                    onClick={() => thumbScrollRef.current?.scrollBy({ left: -140, behavior: "smooth" })}
                    className="absolute -left-2 z-10 p-1.5 rounded-full bg-background/90 hover:bg-background border border-border shadow-xs text-foreground"
                    title="Scroll thumbnails left"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                )}

                <div
                  ref={thumbScrollRef}
                  className="flex items-center gap-2.5 overflow-x-auto scrollbar-none py-1 px-1 w-full scroll-smooth"
                >
                  {images.map((im, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => {
                        setActiveImgIdx(i);
                        setIsPaused(true);
                      }}
                      className={`relative flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 p-1 bg-card rounded-lg border-2 transition overflow-hidden flex items-center justify-center ${
                        i === activeImgIdx
                          ? "border-terracotta ring-2 ring-terracotta/20 shadow-xs"
                          : "border-border opacity-70 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={normaliseImageUrl(im, 200)}
                        className="w-full h-full object-contain"
                        alt=""
                        loading="lazy"
                        decoding="async"
                        referrerPolicy="no-referrer"
                        onError={(e) => handleImageError(e, im, 200)}
                      />
                    </button>
                  ))}
                </div>

                {images.length > 4 && (
                  <button
                    type="button"
                    onClick={() => thumbScrollRef.current?.scrollBy({ left: 140, behavior: "smooth" })}
                    className="absolute -right-2 z-10 p-1.5 rounded-full bg-background/90 hover:bg-background border border-border shadow-xs text-foreground"
                    title="Scroll thumbnails right"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Status & Auto-scroll / Manual scroll controls */}
              <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Auto-slides · Swipe or click to scroll manually
                </span>
                <button
                  type="button"
                  onClick={() => setIsPaused((prev) => !prev)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-border bg-card hover:bg-muted text-[11px] font-medium transition cursor-pointer"
                  title={isPaused ? "Resume auto-scroll" : "Pause auto-scroll"}
                >
                  {isPaused ? (
                    <>
                      <Play className="w-3 h-3 text-emerald-600" /> Play auto-scroll
                    </>
                  ) : (
                    <>
                      <Pause className="w-3 h-3 text-terracotta" /> Pause auto-scroll
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right: Product Details & Specifications */}
        <div className="min-w-0 w-full">
          <div className="flex items-center justify-between mb-2">
            <span className="overline text-terracotta">{p.category}</span>
            {shippingCharge === 0 ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full">
                <Truck className="w-3.5 h-3.5" /> Free Shipping
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
                <Truck className="w-3.5 h-3.5" /> Shipping: <span className="font-price font-semibold">₹{shippingCharge}</span> (Added at checkout)
              </span>
            )}
          </div>

          <h1 data-testid="product-title" className="text-3xl lg:text-5xl font-serif font-semibold tracking-tight mb-4">{p.name}</h1>

          {/* Pricing display */}
          <div className="flex items-baseline gap-3 mb-6">
            <span className="font-price text-3xl md:text-4xl font-bold tracking-tight text-foreground">₹{price}</span>
            {hasDiscount && (
              <>
                <span className="font-price text-lg text-muted-foreground line-through">MRP ₹{mrp}</span>
                <span className="font-price text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded">
                  {discountPct}% OFF
                </span>
              </>
            )}
          </div>

          {/* Description */}
          {p.description && (
            <p className="text-muted-foreground leading-relaxed mb-6 whitespace-pre-line">{p.description}</p>
          )}

          {/* Structured Product Specifications */}
          {hasAnySpec && (
            <div className="mb-6 border border-border rounded-xl p-4 bg-muted/20 space-y-2.5">
              <div className="text-xs font-semibold uppercase tracking-wider text-terracotta">
                Product Specifications
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 text-sm">
                {bagType && (
                  <div className="bg-background border border-border/80 rounded-lg p-2.5">
                    <div className="text-[11px] text-muted-foreground font-medium flex items-center gap-1.5 mb-0.5">
                      <ShoppingBag className="w-3.5 h-3.5 text-terracotta" /> Bag Type
                    </div>
                    <div className="font-semibold text-foreground text-xs sm:text-sm">{bagType}</div>
                  </div>
                )}
                {material && (
                  <div className="bg-background border border-border/80 rounded-lg p-2.5">
                    <div className="text-[11px] text-muted-foreground font-medium flex items-center gap-1.5 mb-0.5">
                      <Layers className="w-3.5 h-3.5 text-terracotta" /> Material
                    </div>
                    <div className="font-semibold text-foreground text-xs sm:text-sm">{material}</div>
                  </div>
                )}
                {colour && (
                  <div className="bg-background border border-border/80 rounded-lg p-2.5">
                    <div className="text-[11px] text-muted-foreground font-medium flex items-center gap-1.5 mb-0.5">
                      <Palette className="w-3.5 h-3.5 text-terracotta" /> Colour
                    </div>
                    <div className="font-semibold text-foreground text-xs sm:text-sm">{colour}</div>
                  </div>
                )}
                {size && (
                  <div className="bg-background border border-border/80 rounded-lg p-2.5">
                    <div className="text-[11px] text-muted-foreground font-medium flex items-center gap-1.5 mb-0.5">
                      <Ruler className="w-3.5 h-3.5 text-terracotta" /> Size / Dimensions
                    </div>
                    <div className="font-semibold text-foreground text-xs sm:text-sm">{size}</div>
                  </div>
                )}
                {weight && (
                  <div className="bg-background border border-border/80 rounded-lg p-2.5">
                    <div className="text-[11px] text-muted-foreground font-medium flex items-center gap-1.5 mb-0.5">
                      <Scale className="w-3.5 h-3.5 text-terracotta" /> Weight
                    </div>
                    <div className="font-semibold text-foreground text-xs sm:text-sm">{weight}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {showVariants && (
            <div className="mb-6 w-full min-w-0">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium text-foreground flex flex-wrap items-center gap-2">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Variant / Color:</span>
                  <span className="font-bold text-foreground">
                    {variant?.tag || "Default"}
                    {parseComboQty(variant?.comboQty) > 1 ? ` (${parseComboQty(variant?.comboQty)} Pcs Combo)` : ""}
                  </span>
                  {variantOut ? (
                    <span className="text-xs font-semibold text-destructive bg-destructive/10 border border-destructive/20 px-2 py-0.5 rounded">
                      Out of Stock
                    </span>
                  ) : (
                    <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
                      In Stock ({variant?.stock})
                    </span>
                  )}
                </div>
                {isImageLoading && (
                  <span className="text-[11px] text-terracotta font-medium flex items-center gap-1.5 animate-pulse">
                    <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-ping" /> Loading…
                  </span>
                )}
              </div>

              {/* Flipkart / E-Commerce Style Variant Image Cards (Only this row scrolls horizontally) */}
              <div
                ref={variantScrollRef}
                className="w-full min-w-0 flex flex-nowrap sm:flex-wrap overflow-x-auto scrollbar-none py-1.5 px-0.5 gap-3 sm:gap-3.5 items-start scroll-smooth touch-pan-x"
                role="radiogroup"
                aria-label="Select product variant"
              >
                {p.variants.map((v, i) => {
                  const vo = Number(v.stock || 0) <= 0;
                  const isSelected = variantIdx === i;
                  const vPrice = v.price != null && v.price !== "" ? Number(v.price) : Number(p.price);
                  const vImg = (v.images && v.images.length > 0 && v.images[0]) || primaryImage(p);

                  return (
                    <button
                      key={i}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      data-testid={`variant-${v.tag}`}
                      onClick={() => handleVariantSelect(i)}
                      className={`group relative shrink-0 flex-shrink-0 flex flex-col items-center p-2 rounded-2xl border-2 transition-all duration-200 bg-card overflow-hidden text-center cursor-pointer active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta ${
                        isSelected
                          ? "border-terracotta ring-2 ring-terracotta/25 shadow-md bg-terracotta/5 scale-[1.03]"
                          : "border-border hover:border-foreground/50 hover:shadow-xs opacity-90 hover:opacity-100"
                      } ${vo ? "bg-muted/30" : ""}`}
                      title={`${v.tag}${vo ? " (Sold Out - Click to view)" : ` - ₹${vPrice}`}`}
                    >
                      {/* Variant Thumbnail Container (Enlarged) */}
                      <div className="relative w-20 h-20 sm:w-24 sm:h-24 md:w-26 md:h-26 rounded-xl overflow-hidden bg-muted/40 border border-border/60 flex items-center justify-center p-1.5 shadow-2xs">
                        <img
                          src={normaliseImageUrl(vImg, 300)}
                          alt={v.tag}
                          className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                          decoding="async"
                          referrerPolicy="no-referrer"
                          onError={(e) => handleImageError(e, vImg, 300)}
                        />

                        {/* Sold out badge */}
                        {vo && (
                          <div className="absolute inset-0 bg-background/70 backdrop-blur-[0.5px] flex items-center justify-center">
                            <span className="text-[10px] font-bold uppercase tracking-tight text-destructive bg-background px-2 py-0.5 rounded shadow-2xs rotate-[-8deg] border border-destructive/30">
                              Sold Out
                            </span>
                          </div>
                        )}

                        {/* Active selection checkmark badge */}
                        {isSelected && (
                          <div className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full ${vo ? "bg-muted-foreground text-background" : "bg-terracotta text-white"} flex items-center justify-center shadow-xs`}>
                            <Check className="w-3 h-3 stroke-[3]" />
                          </div>
                        )}
                      </div>

                      {/* Variant Name & Price Tag */}
                      <div className="w-full text-center px-1 pt-2 pb-0.5 max-w-[84px] sm:max-w-[104px]">
                        <div
                          className={`text-xs sm:text-sm font-semibold truncate leading-tight ${
                            isSelected ? "text-terracotta" : "text-foreground"
                          }`}
                        >
                          {v.tag}
                        </div>
                        {parseComboQty(v.comboQty) > 1 && (
                          <div className="text-[10px] text-muted-foreground font-medium truncate mt-0.5">
                            {parseComboQty(v.comboQty)} Pcs Combo
                          </div>
                        )}
                        <div className="text-xs font-price font-bold text-muted-foreground mt-1">
                          ₹{vPrice}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center border border-border rounded-md overflow-hidden">
              <button
                data-testid="pd-dec"
                disabled={soldOut || variantOut}
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="w-10 h-10 hover:bg-muted text-lg font-medium disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none transition"
              >
                −
              </button>
              <span data-testid="pd-qty" className="w-10 text-center font-price font-bold text-base">
                {soldOut || variantOut ? 0 : qty}
              </span>
              <button
                data-testid="pd-inc"
                disabled={soldOut || variantOut}
                onClick={() => setQty(qty + 1)}
                className="w-10 h-10 hover:bg-muted text-lg font-medium disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none transition"
              >
                +
              </button>
            </div>
            <span className="text-sm font-medium">
              {soldOut || variantOut ? (
                <span className="text-destructive font-semibold">
                  Sold out{showVariants && variant?.tag ? ` (${variant.tag})` : ""}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {variant?.stock ?? p.stock} units available{showVariants ? ` (${variant?.tag})` : ""}
                </span>
              )}
            </span>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              data-testid="pd-add-to-cart"
              disabled={soldOut || variantOut}
              aria-disabled={soldOut || variantOut}
              onClick={() => {
                if (soldOut || variantOut) return;
                add(p, variant, qty);
                toast.success(`Added ${qty} × ${p.name}${showVariants ? ` (${variant.tag})` : ""}`);
              }}
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-foreground text-background hover:bg-terracotta disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none font-medium transition"
            >
              <ShoppingBag className="w-4 h-4" /> {soldOut || variantOut ? "Sold out" : "Add to cart"}
            </button>
            <button
              data-testid="pd-buy-now"
              disabled={soldOut || variantOut}
              aria-disabled={soldOut || variantOut}
              onClick={() => {
                if (soldOut || variantOut) return;
                add(p, variant, qty);
                nav("/checkout");
              }}
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-terracotta text-primary-foreground hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none font-medium transition"
              title={soldOut || variantOut ? "This item is sold out" : "Buy now"}
            >
              {soldOut || variantOut ? "Sold out" : "Buy now"}
            </button>
            {p.videoUrl && (
              <a
                data-testid="pd-youtube"
                href={p.videoUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-border hover:bg-foreground hover:text-background transition"
              >
                <Youtube className="w-4 h-4 text-[#FF0000]" />
                Watch Video
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Full-screen Lightbox Modal */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute top-5 right-5 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition z-50"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="relative max-w-4xl max-h-[85vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={normaliseImageUrl(images[activeImgIdx])}
              alt={p.name}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={handlePrev}
                  className="absolute left-2 sm:-left-12 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center border border-white/20 shadow-lg"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="absolute right-2 sm:-right-12 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center border border-white/20 shadow-lg"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

